import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import { ClientReview } from './../../types';
import './GeneralStats.scss';
import Leaderboards from './components/Leaderboards/Leaderboards';

interface Props {
  reviews: ClientReview[];
}

type OverviewState = {
  total: number;
  avgAm: number;
  avgSa: number;
  avgEx: number;
  avgAll: number;
  modeAll: number | null;
  modeAm: number | null;
  modeSa: number | null;
  modeEx: number | null;
};

interface GeneralStatsProps extends Props {
  startDate?: string;
  endDate?: string;
}

const GeneralStats: React.FC<GeneralStatsProps> = ({ reviews, startDate, endDate }) => {
  // theme - get text color from CSS variable
  const [textColor, setTextColor] = useState('#6b6b6b');
  const [colorMode, setColorMode] = useState(localStorage.getItem('colorMode') || 'dark');

  useEffect(() => {
    const updateTextColor = () => {
      const color = getComputedStyle(document.documentElement)
        .getPropertyValue('--text-color')
        .trim();
      if (color) {
        setTextColor(color);
      }
      const mode = localStorage.getItem('colorMode') || 'dark';
      setColorMode(mode);
    };
    
    updateTextColor();
    // Update when theme changes
    const observer = new MutationObserver(updateTextColor);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-color-mode', 'class']
    });
    
    return () => observer.disconnect();
  }, []);

  const [overview, setOverview] = useState<OverviewState>({
    total: 0,
    avgAm: 0,
    avgSa: 0,
    avgEx: 0,
    avgAll: 0,
    modeAll: null,
    modeAm: null,
    modeSa: null,
    modeEx: null,
  });
  const [historyOption, setHistoryOption] = useState<any>(null);

  // Helper to produce a vertical linear gradient for an rgb string like '252,117,86'
  const makeAreaGradient = (rgb: string, topAlpha = 0.35, bottomAlpha = 0.06) => ({
    type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: `rgba(${rgb},${topAlpha})` },
      { offset: 1, color: `rgba(${rgb},${bottomAlpha})` },
    ],
  });

  // Helper to compute mode of an array of numbers
  const computeMode = (arr: number[]): number | null => {
    if (!arr.length) return null;
    const freqMap: Record<number, number> = {};
    arr.forEach((x) => {
      freqMap[x] = (freqMap[x] || 0) + 1;
    });
    let maxCount = 0;
    let modeVal: number = arr[0];
    Object.entries(freqMap).forEach(([valStr, count]) => {
      const val = Number(valStr);
      if (count > maxCount || (count === maxCount && val > modeVal)) {
        maxCount = count;
        modeVal = val;
      }
    });
    return modeVal;
  };

  useEffect(() => {
    if (!reviews.length) return;

    // 1) OVERVIEW: compute sums, averages, and modes
    const n = reviews.length;
    const amArr: number[] = [];
    const saArr: number[] = [];
    const exArr: number[] = [];
    const allArr: number[] = [];

    reviews.forEach((r) => {
      amArr.push(r.object.amabilidad.rating);
      saArr.push(r.object.sabor.rating);
      exArr.push(r.object.experiencia.rating);
      const overall = (
        (r.object.amabilidad.rating +
          r.object.sabor.rating +
          r.object.experiencia.rating) /
        3
      );
      // Round overall to two decimals for consistency and frequency
      allArr.push(parseFloat(overall.toFixed(2)));
    });

    const sumAm = amArr.reduce((s, x) => s + x, 0);
    const sumSa = saArr.reduce((s, x) => s + x, 0);
    const sumEx = exArr.reduce((s, x) => s + x, 0);

    const avgAm = sumAm / n;
    const avgSa = sumSa / n;
    const avgEx = sumEx / n;
    const avgAll = allArr.reduce((s, x) => s + x, 0) / allArr.length;

    const modeAm = computeMode(amArr);
    const modeSa = computeMode(saArr);
    const modeEx = computeMode(exArr);
    const modeAll = computeMode(allArr);

    setOverview({
      total: n,
      avgAm,
      avgSa,
      avgEx,
      avgAll,
      modeAm,
      modeSa,
      modeEx,
      modeAll,
    });


    // 2) Agrupación por día, semana, mes o año según rango
    let groupType: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'monthly';
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
      if (diffDays <= 7) groupType = 'daily';
      else if (diffDays <= 31) groupType = 'weekly';
      else if (diffDays <= 366) groupType = 'monthly';
      else groupType = 'yearly';
    }

    let byGroupAll: Record<string, number[]> = {};
    let byGroupType: Record<string, Record<number, number[]>> = {};

    // Helper para obtener la semana ISO (YYYY-WW)
    const getISOWeek = (dateStr: string) => {
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      // jueves de la semana actual
      d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
      const week1 = new Date(d.getFullYear(), 0, 4);
      // número de semana ISO
      const weekNum =
        1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
      return `${d.getFullYear()}-W${weekNum.toString().padStart(2, '0')}`;
    };

    reviews.forEach((r) => {
      let groupKey: string;
      if (groupType === 'daily') {
        groupKey = r.timestamp.slice(0, 10); // "YYYY-MM-DD"
      } else if (groupType === 'weekly') {
        groupKey = getISOWeek(r.timestamp);
      } else if (groupType === 'monthly') {
        groupKey = r.timestamp.slice(0, 7); // "YYYY-MM"
      } else {
        groupKey = r.timestamp.slice(0, 4); // "YYYY"
      }
      const overall =
        (r.object.amabilidad.rating +
          r.object.sabor.rating +
          r.object.experiencia.rating) /
        3;
      const overallRounded = parseFloat(overall.toFixed(2));

      if (!byGroupAll[groupKey]) byGroupAll[groupKey] = [];
      byGroupAll[groupKey].push(overallRounded);

      if (!byGroupType[groupKey]) byGroupType[groupKey] = {};
      if (!byGroupType[groupKey][r.pedido_type_id])
        byGroupType[groupKey][r.pedido_type_id] = [];
      byGroupType[groupKey][r.pedido_type_id].push(overallRounded);
    });

    // Si es semanal o diaria, asegurar que todos los grupos estén presentes
    let groups: string[] = Object.keys(byGroupAll);
    if ((groupType === 'weekly' || groupType === 'daily') && startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (groupType === 'weekly') {
        // Ajustar al primer día de la semana ISO (lunes)
        const getMonday = (d: Date) => {
          const date = new Date(d);
          const day = date.getDay();
          const diff = (day === 0 ? -6 : 1) - day;
          date.setDate(date.getDate() + diff);
          return date;
        };
        let current = getMonday(start);
        const weeksSet = new Set(groups);
        while (current <= end) {
          const iso = getISOWeek(current.toISOString().slice(0, 10));
          if (!weeksSet.has(iso)) {
            byGroupAll[iso] = [];
            byGroupType[iso] = {};
          }
          current.setDate(current.getDate() + 7);
        }
        groups = Object.keys(byGroupAll).sort();
      } else if (groupType === 'daily') {
        // Generar todos los días entre startDate y endDate
        let current = new Date(start);
        const daysSet = new Set(groups);
        while (current <= end) {
          const dayStr = current.toISOString().slice(0, 10);
          if (!daysSet.has(dayStr)) {
            byGroupAll[dayStr] = [];
            byGroupType[dayStr] = {};
          }
          current.setDate(current.getDate() + 1);
        }
        groups = Object.keys(byGroupAll).sort();
      }
    } else {
      groups = Object.keys(byGroupAll).sort();
    }

    // Etiquetas personalizadas para el eje X
    const dayNames = ['D', 'L', 'M', 'X', 'J', 'V', 'S']; // Domingo a Sábado
    const formatGroupLabel = (g: string) => {
      if (groupType === 'daily') {
        // g: YYYY-MM-DD
        const d = new Date(g);
        return dayNames[d.getDay()];
      } else if (groupType === 'weekly') {
        // g: YYYY-WWW
        // Mostrar S1, S2, S3... continuo en todo el rango
        const idx = groups.indexOf(g);
        return `S${idx + 1}`;
      } else if (groupType === 'monthly') {
        // g: YYYY-MM
        const [yyyy, mm] = g.split('-');
        const yy = yyyy.slice(-2);
        return `${mm}/${yy}`;
      } else {
        // yearly: g: YYYY
        return g;
      }
    };

    const xLabels = groups.map(formatGroupLabel);

    const allSeriesData = groups.map((g) => {
      const arr = byGroupAll[g];
      if (!arr || arr.length === 0) return 0;
      return parseFloat(
        (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2)
      );
    });

    const typeNames: Record<number, string> = {
      1: 'Restaurant',
      // 2: 'Autoservicio', // Comentado por solicitud
      3: 'Delivery',
      4: 'Caja',
    };

    const palette = [
      '252,117,86', // Todos (primary)
      '239,81,72',  // Restaurant (deep red-orange)
      '255,195,0',  // Delivery (golden orange)
      '255,220,117', // Caja (soft peach)
    ];

    const typeSeries = Object.entries(typeNames).map(([key, name], idx) => {
      const pid = parseInt(key, 10);
      const rgb = palette[idx + 1];
      const data = groups.map((g) => {
        const arr = byGroupType[g]?.[pid] || [];
        if (!arr || arr.length === 0) return 0;
        return parseFloat(
          (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2)
        );
      });
      return {
        name,
        type: 'line' as const,
        smooth: true,
        connectNulls: true,
        showSymbol: true,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { color: `rgb(${rgb})`, width: 3 },
        itemStyle: { color: `rgb(${rgb})`, borderColor: `rgb(${rgb})` },
        areaStyle: { color: makeAreaGradient(rgb) },
        emphasis: { focus: 'series' },
        data,
      };
    });

    const legendItems = ['Todos', ...Object.values(typeNames)];
    const selected: Record<string, boolean> = {};
    legendItems.forEach((item) => {
      selected[item] = item === 'Todos';
    });

    setHistoryOption({
      title: {
        text:
          groupType === 'daily'
            ? 'Tendencia diaria'
            : groupType === 'weekly'
            ? 'Tendencia semanal en el mes'
            : groupType === 'monthly'
            ? 'Tendencia mensual en el año'
            : 'Tendencia anual',
        left: 'center',
        textStyle: { color: textColor, fontSize: 16, fontWeight: 600 },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: colorMode === 'dark' ? 'rgba(20,20,20,0.95)' : '#ffffff',
        borderColor: colorMode === 'dark' ? '#333' : '#eee',
        borderWidth: 1,
        textStyle: { color: colorMode === 'dark' ? '#fff' : '#6b6b6b' },
        axisPointer: {
          type: 'line',
          lineStyle: { color: '#FC7556', width: 1.5, type: 'dashed' },
        },
        extraCssText: 'box-shadow: 0 8px 24px rgba(0,0,0,0.15); border-radius: 8px;'
      },
      legend: {
        data: legendItems,
        selected,
        top: 30,
        textStyle: { 
          color: textColor, 
          fontWeight: 600 
        },
        type: 'scroll',
        itemWidth: 12,
        itemHeight: 8,
        icon: 'roundRect',
      },
      grid: { left: 60, right: 40, bottom: 60, top: 70 },
      xAxis: {
        type: 'category',
        name: 'Fecha',
        nameLocation: 'middle',
        nameGap: 30,
        data: xLabels,
        axisLine: { lineStyle: { color: textColor } },
        axisLabel: { color: textColor },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: 'Calificación',
        nameLocation: 'middle',
        nameGap: 40,
        min: 'dataMin',
        max: 'dataMax',
        axisLine: { lineStyle: { color: textColor } },
        axisLabel: { color: textColor },
        splitLine: {
          show: false,
        },
      },

      series: [
        {
          name: 'Todos',
          type: 'line' as const,
          smooth: true,
          connectNulls: true,
          showSymbol: true,
          symbol: 'circle',
          symbolSize: 8,
          data: allSeriesData,
          lineStyle: { color: `rgb(${palette[0]})`, width: 3 },
          itemStyle: {
            color: `rgb(${palette[0]})`,
            borderColor: `rgb(${palette[0]})`,
          },
          areaStyle: { color: makeAreaGradient(palette[0]) },
          emphasis: { focus: 'series' },
        },
        ...typeSeries,
      ],
    });
  }, [reviews, textColor, colorMode, startDate, endDate]);

  if (!historyOption) return <MiniLoadingSpinner />;

  return (
    <div className="general-stats">
      {/* ========== TOP SECTION: CHART + OVERVIEW CARDS ========== */}
      <div className="top-section">
        {/* ─── Chart Container ─── */}
        <div className="chart">
          <ReactECharts option={historyOption} />
        </div>

        {/* ─── Overview Container (5 KPI cards: 2 above, 3 below) ─── */}
        <div className="overview-container">
          <div className="reviews-kpi-grid reviews-kpi-top">
            {/* Reseñas */}
            <div className="reviews-kpi-card">
              <div className="reviews-kpi-header">
                <span className="reviews-kpi-label">Reseñas</span>
              </div>
              <div className="reviews-kpi-value">{overview.total}</div>
              <div className="reviews-kpi-sub">
                <span className="reviews-kpi-chip">Evaluaciones recibidas</span>
              </div>
            </div>

            {/* Promedio */}
            <div className="reviews-kpi-card">
              <div className="reviews-kpi-header">
                <span className="reviews-kpi-label">Promedio</span>
              </div>
              <div className="reviews-kpi-value">{overview.avgAll.toFixed(2)}</div>
              <div className="reviews-kpi-sub">
                <span className="reviews-kpi-chip">De 5 estrellas</span>
              </div>
            </div>
          </div>

          <div className="reviews-kpi-grid reviews-kpi-bottom">
            {/* Amabilidad */}
            <div className="reviews-kpi-card">
              <div className="reviews-kpi-header">
                <span className="reviews-kpi-label">Amabilidad</span>
              </div>
              <div className="reviews-kpi-value">{overview.avgAm.toFixed(2)}</div>
              {overview.modeAm !== null && (
                <div className="reviews-kpi-sub">
                  <span className="reviews-kpi-chip">Más común {overview.modeAm}</span>
                </div>
              )}
            </div>

            {/* Sabor */}
            <div className="reviews-kpi-card">
              <div className="reviews-kpi-header">
                <span className="reviews-kpi-label">Sabor</span>
              </div>
              <div className="reviews-kpi-value">{overview.avgSa.toFixed(2)}</div>
              {overview.modeSa !== null && (
                <div className="reviews-kpi-sub">
                  <span className="reviews-kpi-chip">Más común {overview.modeSa}</span>
                </div>
              )}
            </div>

            {/* Experiencia */}
            <div className="reviews-kpi-card">
              <div className="reviews-kpi-header">
                <span className="reviews-kpi-label">Experiencia</span>
              </div>
              <div className="reviews-kpi-value">{overview.avgEx.toFixed(2)}</div>
              {overview.modeEx !== null && (
                <div className="reviews-kpi-sub">
                  <span className="reviews-kpi-chip">Más común {overview.modeEx}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ========== LEADERBOARDS (unchanged) ========== */}
      <Leaderboards reviews={reviews} />
    </div>
  );
};

export default GeneralStats;
